import { wrappedApiGet, wrappedApiPost, wrappedApiPut } from 'utils/http';
import { CreateInvoiceRequestV3 } from '@payer-admin-ui/api/src/invoices/requests/CreateInvoiceRequestV3';
import {
  InternalInvoiceResponseV3,
  InvoicePageResponse,
  ResponseInvoiceV3,
} from '@payer-admin-ui/api/src/invoices/responses/InvoiceResponseV3';
import { InvoiceNumberAndDeliveryTypeObject, InvoiceNumberObject } from '@payer-admin-ui/components-v2/src/types/state';
import { CountryCode } from '@payer-admin-ui/api/src/invoices/models/CountryCode';
import { GetAddressResponse } from '@payer-admin-ui/api/src/customers/responses/GetAddressResponse';
import { ManuallyCreditInvoiceRequest } from '@payer-admin-ui/api/src/invoices/requests/ManuallyCreditInvoiceRequest';
import { ManuallyCreditInvoiceExVatRequest } from '@payer-admin-ui/api/src/invoices/requests/ManuallyCreditInvoiceExVatRequest';
import { FullyCreditInvoiceRequest } from '@payer-admin-ui/api/src/invoices/requests/FullyCreditInvoiceRequest';
import { AddManualInvoiceCreditUsageRequest } from '@payer-admin-ui/api/src/invoices/requests/AddManualInvoiceCreditUsageRequest';
import { UpdateInvoiceRequest } from '@payer-admin-ui/api/src/invoices/requests/UpdateInvoiceRequest';
import { FinaliseInvoiceRequestV3 } from '@payer-admin-ui/api/src/invoices/requests/FinaliseInvoiceRequestV3';
import { AddManualInvoiceCreditUsageResponse } from '@payer-admin-ui/api/src/invoices/responses/AddManualInvoiceCreditUsageResponse';
import { AddManualInvoicePaymentRequest } from '@payer-admin-ui/api/src/invoices/requests/AddManualInvoicePaymentRequest';
import { BadDebtInvoicePaymentRequest } from '@payer-admin-ui/api/src/invoices/requests/BadDebtInvoicePaymentRequest';
import { PayWithCreditInvoiceRequest } from '@payer-admin-ui/api/src/frontendspecific/requests/PayWithCreditInvoiceRequest';
import { FrontendSpecificPayWithCreditResponse } from '@payer-admin-ui/api/src/frontendspecific/responses/FrontendSpecificPayWithCreditResponse';
import { AddManualInvoicePaymentResponse } from '@payer-admin-ui/api/src/invoices/responses/AddManualInvoicePaymentResponse';
import { toCentsOrNull } from '@payer-admin-ui/components-v2/src/functions/numbers';
import { InvoiceFilters } from '../views/Pages/invoices/invoicesGlobalState';

const invoiceAPI = {
  fetchPage: ({
    page,
    pageSize,
    filters,
    sort,
    overdue,
  }: {
    page: number;
    pageSize: number;
    filters: InvoiceFilters;
    sort: string[];
    overdue?: boolean;
  }): Promise<InvoicePageResponse> => {
    return wrappedApiGet<InvoicePageResponse>(`/invoices`, {
      query: {
        offset: page * pageSize,
        pageSize,
        overdue,
        invoiceType: filters.invoiceType,
        invoiceStatus: filters.invoiceStatus,
        currencyCode: filters.currencyCode,
        paymentStatus: filters.paymentStatus,
        orderStatus: filters.orderStatus,
        invoiceDateStart: filters.invoiceDateStart,
        invoiceDateEnd: filters.invoiceDateEnd,
        dueDateBefore: filters.dueDateBefore || undefined,
        amountStart: toCentsOrNull(filters.amountStart),
        amountEnd: toCentsOrNull(filters.amountEnd),
        orderApprovalDateStart: filters.orderApprovalDateStart,
        orderApprovalDateEnd: filters.orderApprovalDateEnd,
        companyName: filters.companyName,
        regNumber: filters.regNumber,
        site: filters.site,
        partOfInvoiceAggregation: filters.partOfInvoiceAggregation,
        search: filters.search,
        sort: sort,
      },
    }).withObjectResponse();
  },

  fetchByInvoiceNumber: ({ invoiceNumber }: { invoiceNumber: number }): Promise<InternalInvoiceResponseV3> => {
    return wrappedApiGet<InternalInvoiceResponseV3>(`/invoices/${invoiceNumber}`).withObjectResponse();
  },

  actions: {
    sendCopy: ({ invoiceNumber, deliveryType }: InvoiceNumberAndDeliveryTypeObject): Promise<void> => {
      return wrappedApiPut(`/invoices/send`, {
        body: { invoiceNumber, deliveryType },
      }).withEmptyResponse();
    },
    sendReminder: ({ invoiceNumber, deliveryType }: InvoiceNumberAndDeliveryTypeObject): Promise<void> => {
      return wrappedApiPost(`/invoices/reminders`, {
        query: { invoiceNumber },
        body: { deliveries: [{ deliveryType }] },
      }).withEmptyResponse();
    },
    cancelReminders: ({ invoiceNumber }: InvoiceNumberObject): Promise<void> => {
      return wrappedApiPost(`/invoices/reminders/cancel`, {
        query: { invoiceNumber },
      }).withEmptyResponse();
    },
    stopDebtCollection: ({ invoiceNumber }: InvoiceNumberObject): Promise<void> => {
      return wrappedApiPost(`/invoices/debt-collection/stop`, {
        query: { invoiceNumber },
      }).withEmptyResponse();
    },
    creditFeeBalance: ({ invoiceNumber }: InvoiceNumberObject): Promise<ResponseInvoiceV3> => {
      return wrappedApiPost<ResponseInvoiceV3>(`/invoices/${invoiceNumber}/credit-fee-balance`).withObjectResponse();
    },
    approveOrder: ({ invoiceNumber }: InvoiceNumberObject): Promise<void> => {
      return wrappedApiPut(`/invoices/approve/${invoiceNumber}`).withEmptyResponse();
    },
    updateInvoice: (updateData: UpdateInvoiceRequest, invoiceNumber: number): Promise<ResponseInvoiceV3> => {
      return wrappedApiPut<ResponseInvoiceV3>(`/invoices/update/${invoiceNumber}`, {
        body: { ...updateData },
      }).withObjectResponse();
    },
    sendForDebtCollection: ({ invoiceNumber }: InvoiceNumberObject): Promise<void> => {
      return wrappedApiPut(`/invoices/debt-collection/send`, {
        query: { invoiceNumber },
      }).withEmptyResponse();
    },
    finaliseInvoice: ({
      invoiceNumber,
      sendInvoice,
    }: InvoiceNumberObject & FinaliseInvoiceRequestV3): Promise<void> => {
      return wrappedApiPost<void>(`/invoices/${invoiceNumber}/final`, {
        body: { sendInvoice },
      }).withEmptyResponse();
    },
    creditInvoiceFully: ({
      invoiceNumber,
      invoiceDate,
    }: Omit<FullyCreditInvoiceRequest, 'standalone'>): Promise<InternalInvoiceResponseV3> => {
      return wrappedApiPost<InternalInvoiceResponseV3>(`/invoices/credit/full`, {
        body: { invoiceNumber, invoiceDate, standalone: false },
      }).withObjectResponse();
    },
    creditInvoiceManually: ({
      invoiceNumber,
      invoiceDate,
      standalone,
      items,
    }: ManuallyCreditInvoiceRequest): Promise<InternalInvoiceResponseV3> => {
      return wrappedApiPost<InternalInvoiceResponseV3>(`/invoices/credit/manual`, {
        body: {
          invoiceNumber,
          invoiceDate,
          standalone,
          items,
        },
      }).withObjectResponse();
    },
    creditInvoiceExVatManually: ({
      invoiceNumber,
      invoiceDate,
      orderReference,
      invoiceCustomer,
      currencyCode,
      languageCode,
      deliveryType,
      standalone,
      items,
    }: ManuallyCreditInvoiceExVatRequest): Promise<InternalInvoiceResponseV3> => {
      return wrappedApiPost<InternalInvoiceResponseV3>(`/invoices/credit/custom`, {
        body: {
          invoiceNumber: invoiceNumber,
          invoiceDate: invoiceDate,
          standalone: standalone,
          items: items,
          orderReference: orderReference,
          currencyCode: currencyCode,
          languageCode: languageCode,
          deliveryType: deliveryType,
          invoiceCustomer: invoiceCustomer,
        },
      }).withObjectResponse();
    },
    pairOff: async ({
      creditInvoiceNumber,
      toPayInvoiceNumber,
      amount,
    }: PayWithCreditInvoiceRequest): Promise<FrontendSpecificPayWithCreditResponse> => {
      return wrappedApiPost<FrontendSpecificPayWithCreditResponse>(`/invoices/pay-with-credit`, {
        body: {
          creditInvoiceNumber,
          toPayInvoiceNumber,
          amount,
        },
      }).withObjectResponse();
    },
    manualCreditUsage: async ({
      invoiceNumber,
      amount,
    }: AddManualInvoiceCreditUsageRequest): Promise<AddManualInvoiceCreditUsageResponse> => {
      return wrappedApiPost<AddManualInvoiceCreditUsageResponse>(`/invoices/manual-credit-usage`, {
        body: {
          invoiceNumber,
          amount,
        },
      }).withObjectResponse();
    },
    manualPayment: async ({
      invoiceNumber,
      amount,
      externalReference,
      bookingDate,
    }: AddManualInvoicePaymentRequest): Promise<AddManualInvoicePaymentResponse> => {
      return wrappedApiPost<AddManualInvoicePaymentResponse>(`/invoices/manual-payment`, {
        body: {
          invoiceNumber,
          amount,
          externalReference,
          bookingDate,
        },
      }).withObjectResponse();
    },
    badDebt: async ({
      invoiceNumber,
      externalReference,
      bookingDate,
    }: BadDebtInvoicePaymentRequest): Promise<AddManualInvoicePaymentResponse> => {
      return wrappedApiPost<AddManualInvoicePaymentResponse>(`/invoices/bad-debt`, {
        body: {
          invoiceNumber,
          externalReference,
          bookingDate,
        },
      }).withObjectResponse();
    },
  },
  createInvoiceV3(request: CreateInvoiceRequestV3): Promise<ResponseInvoiceV3> {
    return wrappedApiPost<ResponseInvoiceV3>(`/invoices`, {
      body: request,
    }).withObjectResponse();
  },
  getInvoicePreviewPdf(request: CreateInvoiceRequestV3): Promise<Blob> {
    return wrappedApiPost<unknown>(`/invoices/preview/pdf`, {
      headers: {
        Accept: 'application/pdf',
      },
      body: request,
      responseType: 'blob',
    }).withBlobResponse();
  },
  getAddress({ countryCode, regNumber }: { countryCode: CountryCode; regNumber: string }): Promise<GetAddressResponse> {
    return wrappedApiGet<GetAddressResponse>(`/invoices/address/${countryCode}/${regNumber}`).withObjectResponse();
  },
};

export { invoiceAPI };
